'use client';
import React, { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface FormData {
  name: string;
  phone: string;
  email: string;
  model: string;
  connection: string;
  os: string;
  issue: string;
}

interface FormErrors {
  name?: string;
  phone?: string;
  email?: string;
}

function validateName(v: string) {
  if (!v.trim()) return 'Full name is required.';
  if (v.trim().length < 2) return 'Name must be at least 2 characters.';
}
function validatePhone(v: string) {
  if (!v.trim()) return 'Phone number is required.';
  if (v.replace(/\D/g, '').length < 7) return 'Enter a valid phone number.';
}
function validateEmail(v: string) {
  if (!v.trim()) return;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Enter a valid email address.';
}

function ContactForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const modelFromUrl = searchParams.get('model') || '';
  const connectionFromUrl = searchParams.get('connection') || '';

  const [form, setForm] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    model: modelFromUrl,
    connection: connectionFromUrl,
    os: '',
    issue: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const errs: FormErrors = {};
    const ne = validateName(form.name);
    const pe = validatePhone(form.phone);
    const ee = validateEmail(form.email);
    if (ne) errs.name = ne;
    if (pe) errs.phone = pe;
    if (ee) errs.email = ee;
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      // Save to Supabase only (Formspree removed)
      const supabase = createClient();

      // Capture visitor IP, location, and device type
      let ip_address = '';
      let location = '';
      try {
        const metaRes = await fetch('/api/visitor-meta', { signal: AbortSignal.timeout(6000) });
        if (metaRes.ok) {
          const metaData = await metaRes.json();
          ip_address = metaData.ip_address || '';
          location = metaData.location || '';
        }
      } catch (_) {}

      let device_type = 'Desktop';
      const ua = navigator.userAgent;
      if (/Mobi|Android|iPhone|iPod/i.test(ua)) device_type = 'Mobile';
      else if (/iPad|Tablet/i.test(ua)) device_type = 'Tablet';

      await supabase.from('alex_chat_submissions').insert({
        customer_name: form.name,
        phone: form.phone,
        alternate_phone: null,
        printer_model: form.model || 'Not specified',
        connection_type: form.connection || 'Not specified',
        device: form.os || 'Not specified',
        usage_type: 'Contact Form',
        printer_plugged: null,
        ip_address: ip_address || null,
        location: location || null,
        device_type: device_type || null,
      });
    } catch (_) {}

    localStorage.setItem('customerDetails', JSON.stringify({
      name: form.name,
      email: form.email,
      phone: form.phone,
      model: form.model,
    }));

    setSubmitted(true);
    setTimeout(() => router.push('/loading'), 1500);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    border: '1.5px solid #d1dde8',
    borderRadius: '10px',
    fontSize: '0.95rem',
    color: '#0d2a45',
    background: '#f8fbff',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: "'Segoe UI', Roboto, sans-serif",
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: 700,
    fontSize: '0.88rem',
    color: '#0d2a45',
    marginBottom: '6px',
  };

  const errorStyle: React.CSSProperties = {
    color: '#dc2626',
    fontSize: '0.8rem',
    fontWeight: 600,
    marginTop: '4px',
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', Roboto, sans-serif", background: '#f0f4f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        .form-input:focus { border-color: #0096D6 !important; background: #fff !important; }
        .submit-btn:hover:not(:disabled) { background: linear-gradient(135deg, #007ab8, #004f88) !important; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(0,150,214,0.45) !important; }
        .submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
      `}</style>

      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #0096D6 0%, #005B99 100%)',
        boxShadow: '0 2px 12px rgba(0,91,153,0.3)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{
          width: 'min(900px, calc(100% - 24px))',
          margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px',
              background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg viewBox="0 0 40 40" width="32" height="32" fill="none">
                <text x="4" y="30" fontSize="26" fontWeight="900" fill="white" fontFamily="Arial, sans-serif">hp</text>
              </svg>
            </div>
            <div>
              <div style={{ color: 'white', fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>Printer Support</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4ade80', display: 'inline-block' }}></span>
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem', fontWeight: 600 }}>Online — Average wait: &lt;1 min</span>
              </div>
            </div>
          </div>
          <Link href="/" style={{ color: 'white', textDecoration: 'none', fontWeight: 700, fontSize: '0.85rem', opacity: 0.9 }}>
            ← Home
          </Link>
        </div>
      </header>

      {/* Main */}
      <main style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px 48px' }}>
        <div style={{ width: '100%', maxWidth: '600px' }}>

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <div style={{
              width: '60px', height: '60px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #0096D6, #005B99)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px', fontSize: '1.6rem',
              boxShadow: '0 8px 24px rgba(0,150,214,0.35)',
            }}>
              🖨️
            </div>
            <h1 style={{ fontSize: 'clamp(1.4rem, 5vw, 1.9rem)', fontWeight: 900, color: '#0d2a45', margin: '0 0 8px' }}>
              Contact Printer Support
            </h1>
            <p style={{ color: '#4a7a9b', fontSize: '0.95rem', fontWeight: 500, margin: 0 }}>
              Fill in your details and a specialist will assist you shortly.
            </p>
          </div>

          {/* Form Card */}
          <div style={{
            background: 'white', borderRadius: '20px',
            boxShadow: '0 8px 40px rgba(0,91,153,0.12)',
            padding: 'clamp(20px, 5vw, 36px)',
          }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
                <h2 style={{ color: '#166534', fontWeight: 800, fontSize: '1.2rem', margin: '0 0 8px' }}>Details Received!</h2>
                <p style={{ color: '#4a7a9b', fontWeight: 500 }}>Redirecting you to the setup guide...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

                  {/* Name */}
                  <div>
                    <label style={labelStyle}>Full Name <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      className="form-input"
                      style={{ ...inputStyle, borderColor: errors.name ? '#dc2626' : '#d1dde8' }}
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                    />
                    {errors.name && <p style={errorStyle}>⚠️ {errors.name}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label style={labelStyle}>Phone Number <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      className="form-input"
                      style={{ ...inputStyle, borderColor: errors.phone ? '#dc2626' : '#d1dde8' }}
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                    />
                    {errors.phone && <p style={errorStyle}>⚠️ {errors.phone}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label style={labelStyle}>Email Address <span style={{ color: '#6b8fa8', fontWeight: 500 }}>(optional)</span></label>
                    <input
                      className="form-input"
                      style={{ ...inputStyle, borderColor: errors.email ? '#dc2626' : '#d1dde8' }}
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                    />
                    {errors.email && <p style={errorStyle}>⚠️ {errors.email}</p>}
                  </div>

                  {/* Printer Model */}
                  <div>
                    <label style={labelStyle}>Printer Model</label>
                    <input
                      className="form-input"
                      style={inputStyle}
                      type="text"
                      name="model"
                      value={form.model}
                      onChange={handleChange}
                      placeholder="e.g. HP DeskJet 2755, HP LaserJet Pro M404n"
                    />
                  </div>

                  {/* Connection Type */}
                  <div>
                    <label style={labelStyle}>Connection Type</label>
                    <select
                      className="form-input"
                      style={inputStyle}
                      name="connection"
                      value={form.connection}
                      onChange={handleChange}
                    >
                      <option value="">Select connection type</option>
                      <option value="Wireless">Wireless</option>
                      <option value="Wired (USB)">Wired (USB)</option>
                      <option value="Bluetooth">Bluetooth</option>
                    </select>
                  </div>

                  {/* Operating System */}
                  <div>
                    <label style={labelStyle}>Operating System</label>
                    <select
                      className="form-input"
                      style={inputStyle}
                      name="os"
                      value={form.os}
                      onChange={handleChange}
                    >
                      <option value="">Select your OS</option>
                      <option value="Windows">Windows</option>
                      <option value="Mac">Mac</option>
                      <option value="Chromebook">Chromebook</option>
                      <option value="iPad">iPad</option>
                      <option value="Mobile">Mobile</option>
                    </select>
                  </div>

                  {/* Issue Description */}
                  <div>
                    <label style={labelStyle}>Describe Your Issue <span style={{ color: '#6b8fa8', fontWeight: 500 }}>(optional)</span></label>
                    <textarea
                      className="form-input"
                      style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }}
                      name="issue"
                      value={form.issue}
                      onChange={handleChange}
                      placeholder="Briefly describe the printer issue you're facing..."
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={submitting}
                    style={{
                      width: '100%',
                      padding: '15px',
                      background: 'linear-gradient(135deg, #0096D6, #005B99)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontWeight: 800,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 6px 20px rgba(0,150,214,0.35)',
                      letterSpacing: '0.3px',
                      marginTop: '4px',
                    }}
                  >
                    {submitting ? '⏳ Submitting...' : '🚀 Submit & Get Support'}
                  </button>

                  <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#8aa5b8', fontWeight: 600, margin: 0 }}>
                    🔒 Your information is encrypted and secure
                  </p>
                </div>
              </form>
            )}
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer style={{ background: 'white', borderTop: '1px solid #ddeef8', padding: '14px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: '0.72rem', color: '#8aa5b8', fontWeight: 600 }}>
          © 2025 PrinterSetupOnline.com — Printer Support &nbsp;·&nbsp;
          <Link href="/privacy" style={{ color: '#0096D6', textDecoration: 'none' }}>Privacy Policy</Link> &nbsp;·&nbsp;
          <Link href="/terms" style={{ color: '#0096D6', textDecoration: 'none' }}>Terms</Link> &nbsp;·&nbsp;
          <Link href="/disclaimer" style={{ color: '#0096D6', textDecoration: 'none' }}>Disclaimer</Link>
        </div>
      </footer>
    </div>
  );
}

export default function ContactPage() {
  return (
    <Suspense fallback={
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4f8' }}>
        <div style={{ color: '#0096D6', fontWeight: 700 }}>Loading...</div>
      </div>
    }>
      <ContactForm />
    </Suspense>
  );
}
